import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import type MailerTemplateModel from 'garaje/models/mailer-template';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

import type { SettingsEmailTemplatesShowRouteModel } from './route';

const ACTIONS = {
  CREATE: 'create',
  MANAGE: 'manage',
  EDIT: 'edit',
};

export default class SettingsEmailTemplatesShowController extends Controller {
  declare model: SettingsEmailTemplatesShowRouteModel;

  queryParams = ['action'];

  @tracked action: string | null = null;

  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  get mailerTemplates(): MailerTemplateModel[] {
    return (this.model.mailerTemplates || []).toArray();
  }

  get isCreateModalOpen(): boolean {
    return this.action === ACTIONS.CREATE;
  }

  set isCreateModalOpen(value: boolean) {
    this.action = value ? ACTIONS.CREATE : null;
  }

  get isManageModalOpen(): boolean {
    return this.action === ACTIONS.MANAGE;
  }

  set isManageModalOpen(value: boolean) {
    this.action = value ? ACTIONS.MANAGE : null;
  }

  get isEditing(): boolean {
    return this.action === ACTIONS.EDIT;
  }

  set isEditing(value: boolean) {
    this.action = value ? ACTIONS.EDIT : null;
  }

  @action
  async saveNewCustomMailerTemplate(title: string): Promise<void> {
    const {
      flashMessages,
      store,
      model: { currentLocation, envoyDefaultTemplate },
    } = this;

    try {
      const customTemplate = store.createRecord('mailer-template', {
        ...envoyDefaultTemplate.childTemplateConfig,
        ...{
          title,
          locations: [currentLocation],
        },
      });

      await customTemplate.save();

      flashMessages.showAndHideFlash('success', `Template "${customTemplate.title}" created!`);

      await this.router.transitionTo(
        'visitors.settings.email-templates.show.custom',
        envoyDefaultTemplate.id,
        customTemplate.id,
        {
          queryParams: {
            action: null,
          },
        },
      );
    } catch (e) {
      flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));

      throw e;
    }
  }

  @action
  async saveRecipients(/* mailerTemplate: MailerTemplateModel[] */): Promise<void> {
    // Stub
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'visitors.settings.email-templates.show': SettingsEmailTemplatesShowController;
  }
}
